import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import {
  useAssetCategories,
  useAssetConditions,
  useAssetLocations,
  useCreateAsset,
} from "../hooks/useAssets";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
};

const DEFAULT_CURRENCY = "AED";
const CURRENCIES = ["AED", "USD", "EUR", "GBP", "INR", "SAR"];

export function NewAssetDrawer({ isOpen, onClose, onCreated }: Props) {
  const create = useCreateAsset();
  const { data: categories = [] } = useAssetCategories();
  const { data: conditions = [] } = useAssetConditions();
  const { data: locations = [] } = useAssetLocations();
  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
    bypassScope: true,
  });
  const companies = companiesResult?.data ?? [];

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [assetTag, setAssetTag] = useState("");
  const [description, setDescription] = useState("");

  const [condition, setCondition] = useState("good");
  const [status, setStatus] = useState("available");
  const [location, setLocation] = useState("");
  const [locationDetail, setLocationDetail] = useState("");

  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [warrantyExpiry, setWarrantyExpiry] = useState("");
  const [insuranceValue, setInsuranceValue] = useState("");

  const [specsOpen, setSpecsOpen] = useState(false);
  const [ram, setRam] = useState("");
  const [storage, setStorage] = useState("");
  const [processor, setProcessor] = useState("");
  const [os, setOs] = useState("");
  const [otherSpecs, setOtherSpecs] = useState("");

  const [companyId, setCompanyId] = useState("");
  const [projectId, setProjectId] = useState("");
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    companyId: companyId || undefined,
  });
  const projects = projectsResult?.data ?? [];
  const [notes, setNotes] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (categories.length > 0 && !category) setCategory(categories[0].value);
  }, [isOpen, categories, category]);

  function reset() {
    setName("");
    setCategory(categories[0]?.value ?? "");
    setBrand("");
    setModel("");
    setSerialNumber("");
    setAssetTag("");
    setDescription("");
    setCondition("good");
    setStatus("available");
    setLocation("");
    setLocationDetail("");
    setPurchaseDate("");
    setPurchasePrice("");
    setCurrency(DEFAULT_CURRENCY);
    setWarrantyExpiry("");
    setInsuranceValue("");
    setSpecsOpen(false);
    setRam("");
    setStorage("");
    setProcessor("");
    setOs("");
    setOtherSpecs("");
    setCompanyId("");
    setProjectId("");
    setNotes("");
    setTagsInput("");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!category) {
      setError("Category is required");
      return;
    }
    setError(null);

    const specifications: Record<string, unknown> = {};
    if (ram.trim()) specifications.ram = ram.trim();
    if (storage.trim()) specifications.storage = storage.trim();
    if (processor.trim()) specifications.processor = processor.trim();
    if (os.trim()) specifications.os = os.trim();
    if (otherSpecs.trim()) specifications.other = otherSpecs.trim();

    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        category,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serialNumber: serialNumber.trim() || null,
        assetTag: assetTag.trim() || null,
        description: description.trim() || null,
        condition,
        status,
        location: location || null,
        locationDetail: locationDetail.trim() || null,
        purchaseDate: purchaseDate
          ? new Date(purchaseDate).toISOString()
          : null,
        purchasePrice: purchasePrice ? Number(purchasePrice) : null,
        purchaseCurrency: currency,
        warrantyExpiry: warrantyExpiry
          ? new Date(warrantyExpiry).toISOString()
          : null,
        insuranceValue: insuranceValue ? Number(insuranceValue) : null,
        specifications:
          Object.keys(specifications).length > 0 ? specifications : null,
        notes: notes.trim() || null,
        tags: tagsInput
          ? tagsInput
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
        companyId: companyId || null,
        projectId: projectId || null,
      });
      onCreated?.(res.asset.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create asset");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Asset</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Asset Details
              </h3>
              <div className="space-y-2">
                <Label htmlFor="ast-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ast-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Ajay's MacBook Pro"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ast-cat">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="ast-cat"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  <option value="">Select a category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ast-brand">Brand</Label>
                  <Input
                    id="ast-brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ast-model">Model</Label>
                  <Input
                    id="ast-model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ast-serial">Serial Number</Label>
                  <Input
                    id="ast-serial"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ast-tag">Asset Tag</Label>
                  <Input
                    id="ast-tag"
                    value={assetTag}
                    onChange={(e) => setAssetTag(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ast-desc">Description</Label>
                <textarea
                  id="ast-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                />
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Condition & Status
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Condition</Label>
                  <Select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                  >
                    {conditions.length === 0 ? (
                      <option value="good">Good</option>
                    ) : (
                      conditions.map((c) => (
                        <option key={c.id} value={c.value}>
                          {c.label}
                        </option>
                      ))
                    )}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="available">Available</option>
                    <option value="in_use">In Use</option>
                    <option value="under_repair">Under Repair</option>
                    <option value="retired">Retired</option>
                    <option value="lost">Lost</option>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    <option value="">— None —</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location Detail</Label>
                  <Input
                    value={locationDetail}
                    onChange={(e) => setLocationDetail(e.target.value)}
                    placeholder="Rack 3, Shelf B"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Purchase Info
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Warranty Expiry</Label>
                  <Input
                    type="date"
                    value={warrantyExpiry}
                    onChange={(e) => setWarrantyExpiry(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>Purchase Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Insurance Value</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={insuranceValue}
                  onChange={(e) => setInsuranceValue(e.target.value)}
                />
              </div>
            </section>

            <section
              className="rounded-md border"
              style={{ borderColor: "hsl(210,30%,88%)" }}
            >
              <button
                type="button"
                onClick={() => setSpecsOpen((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-foreground"
              >
                <span>Specifications (optional)</span>
                {specsOpen ? (
                  <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
                ) : (
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                )}
              </button>
              {specsOpen && (
                <div
                  className="space-y-3 border-t p-3"
                  style={{ borderColor: "hsl(210,30%,92%)" }}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>RAM</Label>
                      <Input
                        value={ram}
                        onChange={(e) => setRam(e.target.value)}
                        placeholder="16 GB"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Storage</Label>
                      <Input
                        value={storage}
                        onChange={(e) => setStorage(e.target.value)}
                        placeholder="512 GB SSD"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Processor</Label>
                      <Input
                        value={processor}
                        onChange={(e) => setProcessor(e.target.value)}
                        placeholder="Apple M2"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>OS</Label>
                      <Input
                        value={os}
                        onChange={(e) => setOs(e.target.value)}
                        placeholder="macOS 14"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Other Specs</Label>
                    <textarea
                      value={otherSpecs}
                      onChange={(e) => setOtherSpecs(e.target.value)}
                      rows={2}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      style={{ borderColor: "hsl(210,30%,85%)" }}
                    />
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Association
              </h3>
              <div className="space-y-2">
                <Label>Company</Label>
                <Select
                  value={companyId}
                  onChange={(e) => {
                    setCompanyId(e.target.value);
                    setProjectId("");
                  }}
                >
                  <option value="">— None —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Notes & Tags
              </h3>
              <div className="space-y-2">
                <Label>Notes</Label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="dev, macbook, refurbished"
                />
              </div>
            </section>

            {error && (
              <div className="rounded-md border bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}
          </div>

          <div
            className="border-t bg-white p-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create Asset"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
